import { Group, Pill, Stack, Text } from '@mantine/core'
import { faBuildingColumns, faLocationDot } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { redactPropsWithClassName, toCurrencyString } from '@phoenix-web/utils/general'
import {
  CLEARED_TO_CLOSE,
  COMPLETED,
  CORRESPONDENT_COMPLETED,
  CORRESPONDENT_IN_PROGRESS,
  FINAL_APPROVED,
  FUNDED,
  getProductLabel,
  PRE_APPROVED,
  TransactionType,
  UNDER_CONTRACT,
} from '@phoenix-web/utils/journey'

const ESTIMATED_BRIDGE_LOAN_VISIBLE = [PRE_APPROVED]
const BRIDGE_LOAN_VISIBLE = [
  UNDER_CONTRACT,
  FINAL_APPROVED,
  CLEARED_TO_CLOSE,
  FUNDED,
  COMPLETED,
  CORRESPONDENT_IN_PROGRESS,
  CORRESPONDENT_COMPLETED,
]

export default function BoxClientInformation({
  currentTimelineStep,
  borrowerName,
  coborrowerName,
  departingPropertyAddress1,
  departingPropertyAddress2,
  estimatedBridgeLoan,
  actualBridgeLoan,
  userIsPartner = true,
  transactionType,
}: {
  currentTimelineStep: string
  borrowerName?: string
  coborrowerName?: string
  departingPropertyAddress1: string
  departingPropertyAddress2: string
  estimatedBridgeLoan?: number | string | null
  actualBridgeLoan?: string | null
  userIsPartner?: boolean
  transactionType: TransactionType
}) {
  const productLabel = getProductLabel(transactionType)
  return (
    <Stack
      gap="xl"
      style={{ borderRadius: 'var(--mantine-radius-sm)' }}
      bd="1px solid light-gray.6"
      w="100%"
      p="lg"
      pb="xl"
    >
      <Text fw={700} size="lg">
        {userIsPartner ? 'Client Information' : 'Borrower Information'}
      </Text>

      <Stack gap="xs">
        <Pill
          styles={{
            label: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
          fz="sm"
          bg="unique.0"
          w="fit-content"
          mih="1.875rem"
          fw={700}
          c="dark.9"
        >
          Primary Borrower
        </Pill>
        <Text {...redactPropsWithClassName}>{borrowerName ? borrowerName : '--'}</Text>
      </Stack>

      {coborrowerName && (
        <Stack gap="xs">
          <Pill
            styles={{
              label: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
            fz="sm"
            bg="unique.1"
            w="fit-content"
            mih="1.875rem"
            fw={700}
            c="dark.9"
          >
            Co-Borrower
          </Pill>
          <Text {...redactPropsWithClassName}>{coborrowerName}</Text>
        </Stack>
      )}

      <Stack gap="xs">
        <Text fw={700}>Departing Property</Text>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <FontAwesomeIcon style={{ marginTop: '0.25rem' }} icon={faLocationDot} />
          <Group gap="0">
            <Text {...redactPropsWithClassName}>{departingPropertyAddress1 + ',\u00A0'}</Text>
            <Text {...redactPropsWithClassName}> {departingPropertyAddress2}</Text>
          </Group>
        </Group>
      </Stack>

      {ESTIMATED_BRIDGE_LOAN_VISIBLE.includes(currentTimelineStep) && estimatedBridgeLoan && (
        <Stack gap="xs">
          <Text fw={700}>Estimated {productLabel}</Text>
          <Group gap="xs">
            <FontAwesomeIcon icon={faBuildingColumns} />
            <Text {...redactPropsWithClassName}>{toCurrencyString(Number(estimatedBridgeLoan))}</Text>
          </Group>
        </Stack>
      )}

      {BRIDGE_LOAN_VISIBLE.includes(currentTimelineStep) && actualBridgeLoan && (
        <Stack gap="xs">
          <Text fw={700}>{productLabel}</Text>
          <Group gap="xs">
            <FontAwesomeIcon icon={faBuildingColumns} />
            <Text {...redactPropsWithClassName}>{toCurrencyString(Number(actualBridgeLoan))}</Text>
          </Group>
        </Stack>
      )}
    </Stack>
  )
}
